import {DatePipe, DecimalPipe} from '@angular/common'
import {Component, effect, inject, OnInit, signal} from '@angular/core'
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms'
import {MatButton, MatIconButton} from '@angular/material/button'
import {MatOption} from '@angular/material/core'
import {MatDialog} from '@angular/material/dialog'
import {MatFormField, MatLabel} from '@angular/material/form-field'
import {MatIcon} from '@angular/material/icon'
import {MatInput} from '@angular/material/input'
import {MatPaginator} from '@angular/material/paginator'
import {MatSelect} from '@angular/material/select'
import {MatSort, MatSortHeader} from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatNoDataRow,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table'
import {MatTooltip} from '@angular/material/tooltip'
import {RouterLink} from '@angular/router'
import {
  APPLICATION_ID,
  IApplication,
  IApplicationAdminData,
  IApplicationSummary,
  IRegistration,
  IRegistrationApplicant,
  LLApplicant,
  TApplicationStatus,
  TRegistrationType
} from '@sparbanken-syd/loan-backend'
import {ConfigService} from '@sparbanken-syd/sparbanken-syd-bankid'
import {BaseTable, WaitComponent} from '@sparbanken-syd/sparbanken-syd-theme'
import {filter, finalize, switchMap} from 'rxjs'
import {LOCAL_STORAGE} from '../../../application/app'
import {StatusIndicatorComponent} from '../../../components/status-indicator/status-indicator.component'

import {LoanService} from '../../../services/loan.service'
import {RegisterService} from '../../../services/register.service'
import {DelegateComponent} from '../../classic/delegate/delegate.component'
import {
  PromiseListDeleteDialogComponent
} from '../../classic/promise-list-delete-dialog/promise-list-delete-dialog.component'
import {EditClassicComponent} from '../edit-classic/edit-classic.component'
import {RegisterLoanPromiseComponent} from '../register-loan-promise/register-loan-promise.component'
import {WINDOW} from '../../../application/shared'

export interface IRegistrationListItem {
  [APPLICATION_ID]: string
  dateOfIssue: number
  validThru: number
  usedAmount: number
  status: TApplicationStatus
  approvedAmount: number | undefined
  type: TRegistrationType | null
  timeStamp: number
  applicantFull?: string
  applicantId?: string
  coApplicantFull?: string
  coApplicantId?: string
  adminData: IApplicationAdminData
  office?: string
  newPropertyType: number
  newPropertyFee: number
  fullyUsed?: boolean
  confirmed?: boolean
  adminInfo?: string
  conditions?: string
  ratio?: number
}

interface StoredFilterValues {
  assignee: string | undefined
  office: string | null
  registerType: TRegistrationType | null
  text: string
  status: TApplicationStatus | 'simpleLoanPromise'
}

const FILTER_VALUE = 'loan-admin-filter'

@Component({
  selector: 'spb-register-list',
  templateUrl: './register-list.component.html',
  styleUrl: './register-list.component.scss',
  imports: [
    MatButton,
    DatePipe,
    MatFormField,
    MatLabel,
    MatSelect,
    ReactiveFormsModule,
    MatOption,
    MatInput,
    RouterLink,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    StatusIndicatorComponent,
    MatIcon,
    MatTooltip,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    DecimalPipe,
    MatNoDataRow,
    MatIconButton
  ],
  providers: [{provide: WINDOW, useValue: globalThis}]
})
export class RegisterListComponent extends BaseTable<IRegistrationListItem> implements OnInit {

  public readonly dialog = inject(MatDialog)

  /**
   * Total approved amount for all filtered loan promises
   * Used for displaying summary information when filtering the list
   */
  public allApprovedAmount = 0

  public columnsToDisplay = [
    'dateOfIssue',
    'validThru',
    'applicantFull',
    'coApplicantFull',
    'status',
    'approvedAmount',
    'usedAmount',
    'remainingAmount',
    'office',
    'assignee',
    'print',
    'delete'
  ]

  public loanPromiseList = signal<IRegistrationListItem[]>([])

  public filter = new FormGroup({
    text: new FormControl<string>(''),
    office: new FormControl<string | null>(null),
    assignee: new FormControl<string | undefined>(undefined),
    status: new FormControl<TApplicationStatus | 'simpleLoanPromise' | null>(null),
    registerType: new FormControl<TRegistrationType | null>(null)
  })

  protected readonly loanService = inject(LoanService)
  protected readonly registerService = inject(RegisterService)

  private readonly configService = inject(ConfigService)
  private readonly storage = inject(LOCAL_STORAGE)

  private readonly window = inject(WINDOW)

  constructor() {
    super()
    // Automatically update the table when loanPromiseList changes
    effect(() => {
      this.items.data = this.loanPromiseList()
    })

    this.items.sortingDataAccessor = (item, property) => {
      if (property === 'remainingAmount') {
        return (item.approvedAmount || 0) - (item.usedAmount || 0)
      }

      if (property === 'assignee') {
        return item.adminData?.assignee
      }
      return item[property]
    }
  }

  public ngOnInit() {
    const waitRef = this.dialog.open(WaitComponent, {
      data: {title: 'Laddar...', progress: true}
    })

    /**
     * Load loan promises and apply stored filters if available.
     */
    this.registerService.getRegistrations().pipe(
      switchMap(() => this.registerService.registeredPromises$)
    ).subscribe({
      next: (registrations) => {
        // Ensure that all registrations have a 'type' property.
        // If a promise is missing the 'type', it is assumed to be a private promise
        // (registrations from loan-fe are considered private by default).
        registrations.forEach(p => {
          p.type ||= 'private' // Assign 'private' if 'type' is undefined or falsy
        })

        this.loanPromiseList.set(this.toList(registrations))

        // Apply stored filter values if present
        const storedFilterValues = JSON.parse(this.storage.getItem(FILTER_VALUE) as string)
        if (storedFilterValues) {
          this.filterValues(storedFilterValues)
          this.filter.setValue(storedFilterValues)
        } else {

          // Apply the current user to the filter
          this.filter.setValue({
            assignee: this.configService.currentUser()?.name,
            office: null,
            registerType: null,
            status: null,
            text: ''
          })
        }

        waitRef.close()
      }
    })

    /**
     * Listen for changes in the filter form and apply the filters.
     */
    this.filter.valueChanges.subscribe((filter) => {

      /**
       * Keep assignee filter only if it matches an existing user's name, otherwise reset it to empty string
       */
      filter.assignee = this.loanService.users().some(user => user.name === filter.assignee)
        ? filter.assignee
        : ''

      this.filterValues(filter as StoredFilterValues)
      this.saveFilterValues(filter as StoredFilterValues)
    })
  }

  /**
   * Opens the delegate dialog for assigning a delegate to the application.
   */
  public delegate(application: IApplicationSummary): void {
    this.dialog.open(DelegateComponent, {
      data: {
        application,
        offices: this.loanService.offices()
      },
      disableClose: true,
      width: '400px'
    })
  }

  /**
   * Opens the register dialog for registering a loan promise.
   */
  public register(id?: string) {
    this.dialog.open(RegisterLoanPromiseComponent, {
      data: id, maxHeight: '98vh',
      minWidth: '895px'
    })
  }

  /**
   * Opens the loan dialog for changing a loan promise used amount.
   */
  public editLoanPromiseValues(loanPromise: IApplication) {
    this.dialog.open(EditClassicComponent, {
      data: loanPromise,
      maxHeight: '98vh',
      minWidth: '895px'
    })
  }

  /**
   * Opens the delete confirmation dialog and removes the selected loan promise.
   */
  public remove(id: string, name: string): void {
    const waitRef = this.dialog.open(WaitComponent, {
      data: {title: 'Raderar', progress: true}
    })

    const dialogRef = this.dialog.open(PromiseListDeleteDialogComponent, {
      data: {name: name, promiseId: id}
    })

    dialogRef.afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.registerService.delete(id)),
        finalize(() => waitRef.close())
      )
      .subscribe({
        next: () => {
          this.removeLoanPromiseFromList(id)
        },
        error: (err) => {
          waitRef.close()
          this.dialog.open(WaitComponent, {
            data: {
              title: 'Någonting gick fel',
              text: 'Fel vid radering',
              closable: true
            }
          })
          console.error('Deletion failed:', err)
        }
      })
  }

  /**
   * Prints the loan document for the given loan promise.
   */
  public print(loanPromise: IApplicationSummary) {
    const ref = this.dialog.open(WaitComponent, {
      data: {
        title: 'Vänligen vänta...', progress: true
      }
    })
    this.registerService.getLoanDocument(loanPromise).subscribe({
      next: res => {
        ref.close()
        this.window.open(res.url, '_blank')
      },
      error: err => {
        ref.close()
        console.error('Document retrieval failed:', err)
      }
    })
  }

  public calculateRemainingAmount(loanPromise: IRegistration): number {
    return loanPromise.approvedAmount > 0
      ? (loanPromise.approvedAmount - (loanPromise.usedAmount || 0))
      : 0
  }

  // Type guard to check if the input is of type IRegistration
  protected isRegistration = (item: IRegistration | IApplication): item is IRegistration => {
    return item.status === 'registered'
  }
  // Type guard to check if the applicant is of type IRegistrationApplicant
  protected isRegistrationApplicant = (applicant: IRegistrationApplicant | LLApplicant): applicant is IRegistrationApplicant => {
    return 'sub' in applicant
  }

  /**
   * Saves the current filter values into local storage.
   */
  private saveFilterValues(filter: StoredFilterValues): void {
    this.storage.setItem(FILTER_VALUE, JSON.stringify(filter))
  }

  /**
   * Filters the data based on the given filter values.
   */
  private filterValues(storedFV: StoredFilterValues): void {
    const filteredData = this.toList(
      this.registerService.registeredPromises$.value.filter((r: IRegistration) => {
        // If the status is "simpleLoanPromise", include both "approved" and "manually_issued"
        const matchesStatus = storedFV.status === 'simpleLoanPromise'
          ? ['approved', 'manually_issued'].includes(r.status)
          : !storedFV.status || r.status === storedFV.status

        return (!storedFV.assignee || r.adminData?.assignee === storedFV.assignee) &&
          (!storedFV.office || r.adminData?.office === storedFV.office) &&
          (!storedFV.registerType || r.type === storedFV.registerType) &&
          matchesStatus
      })
    )

    this.loanPromiseList.set(filteredData)
    this.allApprovedAmount = this.loanPromiseList().reduce((acc, cur) => acc + (cur.approvedAmount || 0), 0)
    this.items.filter = storedFV.text
  }

  /**
   * Removes a loan promise from the list by filtering it out.
   */
  private removeLoanPromiseFromList(id: string): void {
    const updatedData = this.loanPromiseList().filter(a => a[APPLICATION_ID] !== id)
    this.loanPromiseList.set(updatedData)
  }

  /**
   * Transforms a list of IRegistration and IApplication objects into IRegistrationListItem objects.
   */
  private toList(input: (IRegistration | IApplication)[]): IRegistrationListItem[] {
    return input.map((i: IRegistration | IApplication) => {
      // Create a result object with defaults and shared properties
      const result: IRegistrationListItem = {
        [APPLICATION_ID]: i[APPLICATION_ID],
        approvedAmount: i.approvedAmount,
        usedAmount: i.usedAmount ?? 0,
        dateOfIssue: i.dateOfIssue ?? 0,
        validThru: i.validThru ?? 0,
        applicantFull: '',
        applicantId: '',
        coApplicantFull: '',
        coApplicantId: '',
        office: i.adminData?.office ?? '',
        status: i.status,
        adminData: i.adminData || {} as IApplicationAdminData,
        type: this.isRegistration(i) ? i.type : 'private',
        timeStamp: i.timeStamp ?? 0,
        newPropertyType: i.property?.type as number,
        newPropertyFee: (i as IApplication).property?.fee as number,
        fullyUsed: i.fullyUsed,
        confirmed: this.isRegistration(i) ? i.confirmed : true,
        adminInfo: i.adminInfo,
        conditions: i.conditions,
        ratio: i.ratio
      }
      // Handle applicant details based on type
      const primaryApplicant = i.applicants?.[0]
      if (primaryApplicant) {
        if (this.isRegistrationApplicant(primaryApplicant)) {
          result.applicantFull = primaryApplicant.name
          result.applicantId = primaryApplicant.sub
        } else {
          result.applicantFull = primaryApplicant.uc?.applicantName
          result.applicantId = primaryApplicant.personNummer
        }
      }

      // Handle co-applicant details if present
      const coApplicant = i.applicants?.[1]
      if (coApplicant) {
        if (this.isRegistrationApplicant(coApplicant)) {
          result.coApplicantFull = coApplicant.name
          result.coApplicantId = coApplicant.sub
        } else {
          result.coApplicantFull = coApplicant.uc?.applicantName
          result.coApplicantId = coApplicant.personNummer
        }
      }
      return result
    })
  }
}